import { getBackendUrl } from '../utils/api';
import React, { useState, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const ChartDatePicker = ({ currentWeek, onWeekChange }) => {
  const { t, currentLang } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentWeek?.year || new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [loading, setLoading] = useState(false);

  const backendUrl = getBackendUrl();

  // Multi-language month names
  const monthNames = {
    el: ['Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος', 'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
    it: ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'],
    es: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  };

  const getMonths = () => monthNames[currentLang] || monthNames.en;

  // Fetch available years on mount
  useEffect(() => {
    fetchAvailableYears();
  }, []);

  // Fetch weeks when year changes
  useEffect(() => {
    if (selectedYear) {
      fetchWeeksForYear(selectedYear);
    }
  }, [selectedYear]);

  const fetchAvailableYears = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/charts/years`);
      const data = await response.json();
      if (data.status === 'success') {
        setAvailableYears(data.years || [selectedYear]);
      }
    } catch (error) {
      console.error('Error fetching years:', error);
      setAvailableYears([selectedYear]);
    }
  };

  const fetchWeeksForYear = async (year) => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/charts/weeks/${year}`);
      const data = await response.json();
      if (data.status === 'success') {
        setAvailableWeeks(data.weeks || []);
      }
    } catch (error) {
      console.error('Error fetching weeks:', error);
      setAvailableWeeks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleWeekSelect = (week) => {
    onWeekChange(week.year, week.week);
    setIsOpen(false);
  };

  const goToPreviousWeek = () => {
    if (currentWeek) {
      let newWeek = currentWeek.week - 1;
      let newYear = currentWeek.year;
      
      if (newWeek < 1) {
        newWeek = 52;
        newYear = currentWeek.year - 1;
      }
      
      onWeekChange(newYear, newWeek);
    }
  };

  const goToNextWeek = () => {
    if (currentWeek) {
      let newWeek = currentWeek.week + 1;
      let newYear = currentWeek.year;
      
      if (newWeek > 52) {
        newWeek = 1;
        newYear = currentWeek.year + 1;
      }
      
      onWeekChange(newYear, newWeek);
    }
  };

  const filterWeeksByMonth = (month) => {
    if (!availableWeeks.length) return [];
    
    return availableWeeks.filter(week => {
      // Parse week_start to get month
      if (week.week_start) {
        const weekMonth = new Date(week.week_start).getMonth() + 1;
        return weekMonth === month;
      }
      return false;
    });
  };

  return (
    <div className="relative">
      {/* Navigation & Current Week Display */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={goToPreviousWeek}
          className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
          title={t('previousWeek')}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-lg shadow-md transition-all"
        >
          <Calendar className="w-5 h-5" />
          <span>
            {currentWeek ? (
              <>
                {t('weekLabel')} {currentWeek.week}, {currentWeek.year}
                {currentWeek.week_range && ` (${currentWeek.week_range})`}
              </>
            ) : (
              t('selectWeek')
            )}
          </span>
        </button>

        <button
          onClick={goToNextWeek}
          className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
          title={t('nextWeek')}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Dropdown Picker */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-96 overflow-auto">
          <div className="p-4">
            {/* Year Selector */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('year')}
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {availableYears.length > 0 ? (
                  availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))
                ) : (
                  <option value={selectedYear}>{selectedYear}</option>
                )}
              </select>
            </div>

            {/* Month Tabs */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('month')} ({t('optional')})
              </label>
              <div className="grid grid-cols-4 gap-2">
                <button
                  onClick={() => setSelectedMonth(null)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedMonth === null
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('allSongs').split(' ')[0]}
                </button>
                {getMonths().map((month, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedMonth(index + 1)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedMonth === index + 1
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {month.slice(0, 3)}
                  </button>
                ))}
              </div>
            </div>

            {/* Week List */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t('weekLabel')}
              </label>
              {loading ? (
                <div className="text-center py-4 text-gray-500">
                  {t('loading')}
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {(selectedMonth 
                    ? filterWeeksByMonth(selectedMonth)
                    : availableWeeks
                  ).length > 0 ? (
                    (selectedMonth 
                      ? filterWeeksByMonth(selectedMonth)
                      : availableWeeks
                    ).map((week) => (
                      <button
                        key={`${week.year}-${week.week}`}
                        onClick={() => handleWeekSelect(week)}
                        className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                          currentWeek?.year === week.year && currentWeek?.week === week.week
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-semibold">
                            {t('week')} {week.week}
                          </span>
                          <span className="text-sm">
                            {week.week_range}
                          </span>
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      {selectedMonth 
                        ? t('noWeeksForMonth', { month: getMonths()[selectedMonth - 1] })
                        : t('noWeeksForYear', { year: selectedYear })
                      }
                      <div className="mt-2 text-sm">
                        {t('chartsSavedWeekly')}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default ChartDatePicker;
